import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ADMIN_ROUTES } from "../../../../constants/routes/admin/menus";

import { FilterMatchMode } from "primereact/api";
import { AppDispatch } from "../../../../store";
import { Toolbar } from "primereact/toolbar";
import { downloadResumeForAdmin, retrieveResumes } from "../ResumeAction";
import LinkButton from "../../../../constants/helpers/LinkButton";
import { Resume, ResumeStatus } from "../interface";
import { Badge } from "primereact/badge";
import { Dropdown } from "primereact/dropdown";
import { ButtonGroup } from "primereact/buttongroup";

interface ResumeManagementProps {
}

const ResumeManagement = (props: ResumeManagementProps) => {
  const { } = props
  const toast = useRef<any>()
  const dt = useRef<any>(null)
  const history = useHistory()

  const dispatch = useDispatch<AppDispatch>()
  const { resumeList, totalRecords } = useSelector((state: any) => state.resume)

  let filters: DataTableFilterMeta = {
    fullName: { value: null, matchMode: FilterMatchMode.EQUALS },
    contactNumber: { value: null, matchMode: FilterMatchMode.EQUALS },
    email: { value: null, matchMode: FilterMatchMode.EQUALS },
    nationality: { value: null, matchMode: FilterMatchMode.EQUALS },
    totalYearsOfExperiences: { value: null, matchMode: FilterMatchMode.EQUALS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    createdDate: { value: null, matchMode: FilterMatchMode.EQUALS },
  }
  const [rows, setRows] = useState(5)
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: rows,
    page: 0,
    sortField: "createdDate",
    sortOrder: -1,
    filters
  })
  const [selectedStatus, setSelectedStatus] = useState(null)

  //Lazy loading
  useEffect(() => {
    dispatch(retrieveResumes(lazyParams))
  }, [lazyParams])

  // Custom Filter Details
  const [selectedDate, setSelectedDate] = useState(undefined)

  const onPage = (event: any) => {
    setRows(event.rows)
    let _lazyParams = { ...lazyParams, ...event }
    setLazyParams(_lazyParams)
  }

  const onSort = (event: any) => {
    let _lazyParams = { ...lazyParams, ...event }
    setLazyParams(_lazyParams)
  }

  const onFilter = (event: any) => {
    let _lazyParams = { ...lazyParams, ...event }
    _lazyParams['first'] = 0;
    setLazyParams(_lazyParams)
  }
  
  const onStatusChange = (e: any) => {
    setSelectedStatus(e.value)
    let _lazyParams = { ...lazyParams }
    _lazyParams.filters.status = { value: e.value, matchMode: FilterMatchMode.EQUALS }
    setLazyParams(_lazyParams)
  }

  const onDateChange = (e: any) => {
    setSelectedDate(e.value)
    if (e.value[0] != null && e.value[1] != null) {
      let startDate = `${e.value[0].getFullYear()}-${e.value[0].getMonth() + 1}-${e.value[0].getDate()}`
      let endDate = `${e.value[1].getFullYear()}-${e.value[1].getMonth() + 1}-${e.value[1].getDate() + 1}`
      let _lazyParams = { ...lazyParams }
      _lazyParams.filters.createdDateLt = { 'value': startDate, matchMode: FilterMatchMode.EQUALS }
      _lazyParams.filters.createdDateGt = { 'value': endDate, matchMode: FilterMatchMode.EQUALS }
      setLazyParams(_lazyParams)
    }
  }

  const dateFilter = <Calendar value={selectedDate} readOnlyInput selectionMode="range" onChange={onDateChange} dateFormat="yy-mm-dd" className="p-column-filter" placeholder="YYYY-MM-DD"></Calendar>

  const handleResetFilters = () => {
    setSelectedDate(undefined)
    dt.current.reset();
    let _lazyParams = { ...lazyParams }
    _lazyParams.filters = {}
    setLazyParams(_lazyParams)
  }

  const toolbar = (
    <>
      <Button type="button" label="Clear" className="p-button-outlined" icon="pi pi-filter-slash" onClick={handleResetFilters} />
      <LinkButton to={"/resumes/upload"} className="p-button-primary ml-2" icon="upload" label="Upload Resumes" />
    </>
  )

  const createdDateBodyTemplate = (rowData: any) => {
    let date = new Date(rowData.createdDate)
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  }

  const handleDownloadResume = (resume: Resume) => () => {
    dispatch(downloadResumeForAdmin(resume.id)).then((link: any) => {
      // Create a new link
      const linkElement = document.createElement('a');
      linkElement.href = link.payload;

      // Set the download name
      linkElement.setAttribute('download', resume.fileName);

      // Programmatically click the link to trigger the download
      document.body.appendChild(linkElement);
      linkElement.click();

      // Remove the link from the body
      document.body.removeChild(linkElement);
    })
  }

  const viewResumeTemplate = (rowData: any) => {
    return (
      <ButtonGroup>
        <LinkButton to={"/resumes/" + rowData.id}
          icon="eye" iconOnly={true}
          tooltip="View Resume" tooltipOptions={{ position: 'top' }} />
        <LinkButton to={"/resumes/" + rowData.id + "/update"}
          icon="pencil" iconOnly={true}
          tooltip="Modify Resume" tooltipOptions={{ position: 'top' }} />
        <Button severity="secondary"
          icon="pi pi-download" onClick={handleDownloadResume(rowData)}
          tooltip="Download Resume" tooltipOptions={{ position: 'top' }} />
      </ButtonGroup>
    )
  }
  
  const statuses = Object.keys(ResumeStatus).filter(k => isNaN(Number(k)));
  const statusFilter = <Dropdown value={selectedStatus} options={statuses} onChange={onStatusChange} placeholder="All" className="p-column-filter" showClear></Dropdown>
  
  const getStatusStyling = (rowData: any) => {
    switch (rowData.status) {
      case ResumeStatus.FILLED_IN:
        return <Badge value={rowData.status} severity="success"></Badge>
      case ResumeStatus.PARTIALLY_FILLED:
        return <Badge value={rowData.status} severity="warning"></Badge>
      case ResumeStatus.MISSING_MANDATORY_FIELDS:
        return <Badge value={rowData.status} severity="danger"></Badge>
    }
  }

  return (
    <div>
      <Toast ref={toast} />
      <div className="datatable-doc-demo">
        <div className="card">
          <Toolbar className="p-mb-4" left={toolbar}></Toolbar>
          <DataTable ref={dt} value={resumeList} first={lazyParams.first} rows={rows} totalRecords={totalRecords}
            onPage={onPage} onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder as any}
            onFilter={onFilter} filters={lazyParams.filters} filterDisplay="row"
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown" rowsPerPageOptions={[5, 10, 25]}
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
            lazy paginator>
            <Column field="fullName" header="Full Name" filter sortable></Column>
            <Column field="contactNumber" header="Contact Number" filter sortable></Column>
            <Column field="email" header="E-Mail" filter sortable></Column>
            <Column field="nationality" header="Nationality" filter sortable></Column>
            <Column field="totalYearsOfExperiences" header="Years of Experience" filter sortable></Column>
            <Column field="status" header="Status" body={getStatusStyling} filter filterElement={statusFilter} sortable></Column>
            <Column field="createdDate" header="Created Date" body={createdDateBodyTemplate} filter filterElement={dateFilter} sortable></Column>
            <Column body={viewResumeTemplate} headerStyle={{ width: '12em', textAlign: 'center' }} bodyStyle={{ textAlign: 'center', overflow: 'visible' }} />
          </DataTable>
        </div>
      </div>
    </div>
  )
}

export default ResumeManagement

